import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { ClickAwayListener } from '@mui/material';
import { EmbersMenuList } from '../../menuList/menuList';
import { EmbersDatePickerView } from './datePickerView';
import { DateMenu } from '../dateStyles';
import { getDateRange, getFormat, handleTabKey, focusToFirstActiveElement } from '../../utils/dateUtils';
import { getI18nString } from '../../utils/langUtils';
import { EmbersMiniSelect } from '../../select/miniSelect/miniSelect';
import { DateRangePopper } from '../dateStyles';
import { useId } from '../../utils/useId';
export var EmbersDateSelect = function EmbersDateSelect(props) {
  var label = props.label,
    onPickRange = props.onPickRange,
    selection = props.selection,
    value = props.value,
    isoWeek = props.isoWeek,
    format = props.format,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$hideCustomPick = props.hideCustomPicker,
    hideCustomPicker = _props$hideCustomPick === void 0 ? false : _props$hideCustomPick,
    _id = props.id,
    disableFuture = props.disableFuture,
    disablePast = props.disablePast;
  var id = useId(_id);
  var intl = useIntl();
  var dateFormat = getFormat(format, props.locale);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    date = _useState2[0],
    setDate = _useState2[1];
  var _useState3 = useState([null, null]),
    _useState4 = _slicedToArray(_useState3, 2),
    range = _useState4[0],
    setRange = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedOption = _useState6[0],
    setSelectedOption = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    selectedValue = _useState8[0],
    setSelectedValue = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    datePickerOpen = _useState10[0],
    setDatePickerOpen = _useState10[1];

  // This is to control not focus to the first menu when selectedValue changed
  var dateIsNotSelectedOnFirstOpened = useRef(false);
  // This is to decide whether autoFocus to Input when menu entered
  var eventTypeOnMenuEnter = useRef('onClick');
  useEffect(function () {
    var _props$selection;
    var selected = (_props$selection = props.selection) === null || _props$selection === void 0 ? void 0 : _props$selection.value;
    if (selected !== (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value)) {
      handleSelectChange(false)(props.selection || null);
      handleClose();
      if (!selected) {
        date && setDate(null);
        range && setRange([null, null]);
      }
    }
    if (selected && selected >= 6 && props.value) {
      if (Array.isArray(props.value)) {
        setRange(props.value);
      } else {
        setDate(props.value);
      }
    }
  }, [props.selection, props.value]);
  var key = 'embers.dateSelect.option.';
  var dateOptions = [{
    value: 1,
    label: getI18nString(intl, key.concat('today'), 'Today')
  }, {
    value: 2,
    label: getI18nString(intl, key.concat('yesterday'), 'Yesterday')
  }, {
    value: 3,
    label: getI18nString(intl, key.concat('lastWeek'), 'Last week')
  }, {
    value: 4,
    label: getI18nString(intl, key.concat('lastMonth'), 'Last month')
  }, {
    value: 5,
    label: getI18nString(intl, key.concat('lastYear'), 'Last year')
  }];
  if (!hideCustomPicker) {
    dateOptions.push({
      value: 6,
      label: getI18nString(intl, key.concat('customDate'), 'Custom date')
    }, {
      value: 7,
      label: getI18nString(intl, key.concat('customRange'), 'Custom range')
    });
  }
  if (selectedOption) {
    dateOptions.unshift({
      value: 0,
      label: getI18nString(intl, 'embers.select.option.none', '-- None --')
    });
  }
  var resetOptionValue = function resetOptionValue() {
    // Clear filter when selecting custom date or range with no value selected
    var selectedOptionValue = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value;
    if (selectedOptionValue && (isEmptyDate(selectedOptionValue) || isEmptyRange(selectedOptionValue))) {
      var selected = selection === null || selection === void 0 ? void 0 : selection.value;
      if (selected) {
        setSelectedOption(selection);
        if (selected >= 6 && value) {
          if (Array.isArray(value)) {
            setRange(value);
          } else {
            setDate(moment(value));
          }
        }
      } else {
        setSelectedOption(null);
        setDate(null);
        setRange([null, null]);
      }
    }
  };
  var isDate = function isDate(value) {
    return value ? value === 6 : selectedValue === 6;
  };
  var isEmptyDate = function isEmptyDate(value) {
    return isDate(value) && date === null;
  };
  var isRange = function isRange(value) {
    return value ? value === 7 : selectedValue === 7;
  };
  var isEmptyRange = function isEmptyRange(value) {
    return isRange(value) && (range[0] === null || range[1] === null);
  };
  var handleDateSelect = function handleDateSelect(selected) {
    setDate(selected);
    onPickRange === null || onPickRange === void 0 || onPickRange(getDateRange(6, [selected], isoWeek), {
      option: dateOptions[6],
      value: selected
    });
    handleClose();
    // focus to select button when close menu
    focusSelectButton();
  };
  var handleDateRangeSelect = function handleDateRangeSelect(range, selected) {
    setRange(selected);
    onPickRange === null || onPickRange === void 0 || onPickRange(range, {
      option: dateOptions[7],
      value: selected
    });
    handleClose();
    // focus to select button when close menu
    focusSelectButton();
  };

  // the select needs to know immediately if it should stay open
  //  so we return that information from the onChange callback
  var handleSelectChange = function handleSelectChange(isManuallySelected) {
    return function (selected, reason, option, event) {
      if (event) {
        eventTypeOnMenuEnter.current = event._reactName;
      }
      if (isManuallySelected && !dateIsNotSelectedOnFirstOpened.current) {
        dateIsNotSelectedOnFirstOpened.current = true;
      }

      // use a local var because datePickerOpen won't change synchronously
      var showingSubmenu = datePickerOpen;
      var value;
      if (selected) {
        value = selected.value;
      }
      if (!isDate(value)) {
        setDate(null);
        showingSubmenu = false;
      }
      if (!isRange(value)) {
        setRange([null, null]);
        showingSubmenu = false;
      }
      // this is handling both the first item - which is "clear the date" and has 0 value -
      //  as well as the case where props.selection has been set to null by the component
      //  which controls this dateSelect
      if (!value) {
        setSelectedValue(null);
        setSelectedOption(null);
        onPickRange === null || onPickRange === void 0 || onPickRange({
          from: '',
          to: ''
        }, {
          option: selected
        });
        handleClose();
        focusSelectButton();
        showingSubmenu = false;
      } else {
        setSelectedValue(value);
        setSelectedOption(selected);
        if (value < 6) {
          onPickRange === null || onPickRange === void 0 || onPickRange(getDateRange(value, undefined, isoWeek), {
            option: selected
          });
          handleClose();
          focusSelectButton();
          showingSubmenu = false;
        } else {
          setDatePickerOpen(true);
          showingSubmenu = true;
        }
      }
      return showingSubmenu;
    };
  };
  var onPopperOpened = useCallback(function () {
    setTimeout(function () {
      var menuList = document.querySelectorAll("#dateMenu-".concat(id, " li"));
      menuList.forEach(function (v) {
        v.setAttribute('tabindex', '-1');
      });
      if (!dateIsNotSelectedOnFirstOpened.current) {
        var _menuList$;
        // @ts-ignore
        (_menuList$ = menuList[0]) === null || _menuList$ === void 0 || _menuList$.focus();
      }
    }, 0);
  }, [id]);
  var onSelectSameOption = function onSelectSameOption(e, selectedIndex) {
    if ((e === null || e === void 0 ? void 0 : e.key) === 'Enter') {
      if (selectedIndex === 6 || selectedIndex === 7) {
        focusToFirstActiveElement("#datePickerView-".concat(id));
      }
    }
  };
  var onKeyDown = function onKeyDown(e) {
    switch (e.key) {
      case 'Tab':
        if (datePickerOpen && (selectedValue === 6 || selectedValue === 7)) {
          var focusToMenu = function focusToMenu(e) {
            var selectedMenu = document.querySelectorAll("#dateMenu-".concat(id, " li"))[selectedValue];
            selectedMenu.focus();
            e.preventDefault();
          };
          handleTabKey(["#datePickerView-".concat(id)], focusToMenu)(e);
        }
        break;
      case 'Escape':
        if (!(selection !== null && selection !== void 0 && selection.value) && (selectedValue === 6 && isEmptyDate(selectedValue) || selectedValue === 7 && isEmptyRange(selectedValue))) {
          setSelectedOption(null);
        }
        handleClose();
        focusSelectButton();
        e.preventDefault();
        break;
    }
  };
  var focusSelectButton = function focusSelectButton() {
    focusToFirstActiveElement("#dateSelectButton-".concat(id));
  };
  var onPickerViewUnmount = function onPickerViewUnmount() {
    dateIsNotSelectedOnFirstOpened.current = false;
  };
  var handleClose = function handleClose() {
    setDatePickerOpen(false);
    setSelectedValue(null);
    onPickerViewUnmount();
  };
  var handleOpen = function handleOpen() {
    var defaultSelection = props.selection;
    var value = defaultSelection === null || defaultSelection === void 0 ? void 0 : defaultSelection.value;
    if (defaultSelection && (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) !== value) {
      setSelectedOption(value === 0 ? null : defaultSelection);
    }
    if (value) {
      setSelectedValue(value);
      if ((isDate(value) || isRange(value)) && !hideCustomPicker) {
        setDatePickerOpen(true);
      }
      if (props.value && value >= 6) {
        if (Array.isArray(props.value)) {
          setRange(props.value);
        } else {
          setDate(props.value);
        }
      }
    }
  };
  var onRangePickerCancel = function onRangePickerCancel() {
    resetOptionValue();
    handleClose();
    // focus to select button when close menu
    focusSelectButton();
  };
  var _renderMenu = function renderMenu(props) {
    var _classnames;
    // the first time open needn't to focus to the picker view
    var calendarViewShowByManuallySelected = dateIsNotSelectedOnFirstOpened.current && datePickerOpen && eventTypeOnMenuEnter.current !== 'onClick';
    return /*#__PURE__*/React.createElement(DateRangePopper, {
      className: isDate() ? 'EmbersDateRangePopper-single' : 'EmbersDateRangePopper-range',
      anchorEl: props.anchorEl,
      open: props.open,
      onEscape: props.onClose,
      disableCloseOnTab: true,
      onOpen: onPopperOpened
    }, /*#__PURE__*/React.createElement(DateMenu, {
      id: "dateMenu-".concat(id),
      key: "DateMenu".concat(selectedValue && selectedValue >= 6 ? "_".concat(selectedValue) : ''),
      className: classnames({
        'EmbersDateSelect-customRangle-selected': selectedValue === 7
      }),
      onKeyDown: onKeyDown
    }, /*#__PURE__*/React.createElement(EmbersMenuList, {
      id: "MiniSelect-menu-".concat(id),
      className: classnames((_classnames = {
        'EmbersDateSelect-menu': true
      }, _defineProperty(_classnames, "".concat(props.className), !!props.className), _defineProperty(_classnames, 'EmbersDateSelect-open', datePickerOpen), _classnames)),
      menuScrollProps: {
        removeScroll: true
      }
    }, props.menuItems), /*#__PURE__*/React.createElement(EmbersDatePickerView, {
      id: "datePickerView-".concat(id),
      open: datePickerOpen,
      isRangeView: isRange(),
      date: isDate() ? date : range,
      format: dateFormat,
      autoFocus: calendarViewShowByManuallySelected,
      onPickerChange: handleDateSelect,
      onRangePickerChange: handleDateRangeSelect,
      onRangePickerCancel: onRangePickerCancel,
      disableFuture: disableFuture,
      disablePast: disablePast
    })));
  };
  return /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: function onClickAway() {
      resetOptionValue();
      handleClose();
    },
    touchEvent: false
  }, /*#__PURE__*/React.createElement("div", {
    className: "EmbersDateSelect-root",
    id: "dateSelectButton-".concat(id),
    role: "presentation"
  }, /*#__PURE__*/React.createElement(EmbersMiniSelect, {
    name: "dateSelect-".concat(id),
    multiple: false,
    disabled: disabled,
    options: dateOptions,
    label: label || '',
    labelKey: "label",
    value: selectedOption ? selectedOption : undefined,
    onChange: handleSelectChange(true),
    onOpen: handleOpen,
    onClose: handleClose,
    isShowingSubmenu: datePickerOpen,
    onSelectSameOption: onSelectSameOption,
    renderMenu: function renderMenu(menuProps) {
      return _renderMenu(menuProps);
    },
    isOptionEqualToValue: function isOptionEqualToValue(option, value) {
      return option.value === value.value;
    }
  })));
};